// TacticalMarkingEditor.jsx — dimension-modifier editor for selected parametric
// tactical symbols (CIRCULAR, ELLIPSE, RECTANGULAR, ARC, CORRIDOR).
// Also exposes the universal Properties modifier section for all tactical elements.
// Reads/writes the sidcModifiers JSON blob on the marking node.

import React, {useMemo, useCallback} from 'react';
import {Box, Stack, Typography, TextField} from '@mui/material';
import {MSLookup, Modifiers} from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import {resolveDrawMode} from '../map/editor/resolveDrawMode';

// DrawRule numeric ranges (must stay in sync with resolveDrawMode.js)
const isCircular    = (dr) => dr >= 901 && dr <= 902;
const isEllipse     = (dr) => dr >= 701 && dr <= 799;
const isArc         = (dr) => dr === 1001;
const isRectangular = (dr) => dr >= 801 && dr <= 803;
const isCorridor    = (dr) => dr >= 401 && dr <= 499;
// POINT family parametric rules
const isPoint17     = (dr) => dr === 217; // rectangle: AM1=length, AM2=width, AN=rotation
const isPoint18     = (dr) => dr === 218; // search-area fan: AN=azimuth, AM=start/stop range

const MODIFIER_LABELS = {
    [Modifiers.A_SYMBOL_ICON]:               'A – Symbol Icon',
    [Modifiers.B_ECHELON]:                   'B – Echelon',
    [Modifiers.C_QUANTITY]:                  'C – Quantity',
    [Modifiers.D_TASK_FORCE_INDICATOR]:      'D – Task Force',
    [Modifiers.E_FRAME_SHAPE_MODIFIER]:      'E – Frame Shape',
    [Modifiers.F_REINFORCED_REDUCED]:        'F – Reinforced/Reduced',
    [Modifiers.G_STAFF_COMMENTS]:            'G – Staff Comments',
    [Modifiers.H_ADDITIONAL_INFO_1]:         'H – Info 1',
    [Modifiers.H1_ADDITIONAL_INFO_2]:        'H1 – Info 2',
    [Modifiers.H2_ADDITIONAL_INFO_3]:        'H2 – Info 3',
    [Modifiers.J_EVALUATION_RATING]:         'J – Evaluation',
    [Modifiers.K_COMBAT_EFFECTIVENESS]:      'K – Combat Effectiveness',
    [Modifiers.L_SIGNATURE_EQUIP]:           'L – Signature Equip',
    [Modifiers.M_HIGHER_FORMATION]:          'M – Higher Formation',
    [Modifiers.N_HOSTILE]:                   'N – Hostile',
    [Modifiers.P_IFF_SIF_AIS]:               'P – IFF/SIF/AIS',
    [Modifiers.Q_DIRECTION_OF_MOVEMENT]:     'Q – Direction',
    [Modifiers.R_MOBILITY_INDICATOR]:        'R – Mobility',
    [Modifiers.R2_SIGNIT_MOBILITY_INDICATOR]:'R2 – SIGINT Mobility',
    [Modifiers.S_HQ_STAFF_INDICATOR]:        'S – HQ Staff',
    [Modifiers.S2_OFFSET_INDICATOR]:         'S2 – Offset',
    [Modifiers.T_UNIQUE_DESIGNATION_1]:      'T – Designation',
    [Modifiers.T1_UNIQUE_DESIGNATION_2]:     'T1 – Designation 2',
    [Modifiers.T2_UNIQUE_DESIGNATION_3]:     'T2 – Designation 3',
    [Modifiers.V_EQUIP_TYPE]:                'V – Equip Type',
    [Modifiers.W_DTG_1]:                     'W – DTG Start',
    [Modifiers.W1_DTG_2]:                    'W1 – DTG End',
    [Modifiers.X_ALTITUDE_DEPTH]:            'X – Alt/Depth',
    [Modifiers.Y_LOCATION]:                  'Y – Location',
    [Modifiers.Z_SPEED]:                     'Z – Speed',
    [Modifiers.AA_SPECIAL_C2_HQ]:            'AA – Special C2 HQ',
    [Modifiers.AB_FEINT_DUMMY_INDICATOR]:    'AB – Feint/Dummy',
    [Modifiers.AD_PLATFORM_TYPE]:            'AD – Platform Type',
    [Modifiers.AE_EQUIPMENT_TEARDOWN_TIME]:  'AE – Teardown Time',
    [Modifiers.AF_COMMON_IDENTIFIER]:        'AF – Common ID',
    [Modifiers.AG_AUX_EQUIP_INDICATOR]:      'AG – Aux Equip',
    [Modifiers.AH_AREA_OF_UNCERTAINTY]:      'AH – Area of Uncertainty',
    [Modifiers.AI_DEAD_RECKONING_TRAILER]:   'AI – Dead Reckoning',
    [Modifiers.AJ_SPEED_LEADER]:             'AJ – Speed Leader',
    [Modifiers.AK_PAIRING_LINE]:             'AK – Pairing Line',
    [Modifiers.AL_OPERATIONAL_CONDITION]:    'AL – Op Condition',
    [Modifiers.AM_DISTANCE]:                 'AM – Distance',
    [Modifiers.AN_AZIMUTH]:                  'AN – Azimuth',
    [Modifiers.AO_ENGAGEMENT_BAR]:           'AO – Engagement Bar',
    [Modifiers.AP_TARGET_NUMBER]:            'AP – Target No.',
    [Modifiers.AP1_TARGET_NUMBER_EXTENSION]: 'AP1 – Target Ext.',
    [Modifiers.AQ_GUARDED_UNIT]:             'AQ – Guarded Unit',
    [Modifiers.AR_SPECIAL_DESIGNATOR]:       'AR – Special Designator',
    [Modifiers.AS_COUNTRY]:                  'AS – Country',
    [Modifiers.AT_CAPACITY_OF_INSTALLATION]: 'AT – Capacity',
    [Modifiers.AV_LEADERSHIP]:               'AV – Leadership',
    [Modifiers.AW_HEADQUARTERS_ELEMENT]:     'AW – HQ Element',
    [Modifiers.AX_INSTALLATION_COMPOSITION]: 'AX – Installation',
    [Modifiers.AY_NETWORK_IDENTIFIER]:       'AY – Network ID',
};

function getDrawRule(sidc) {
    try {
        const lookup = MSLookup.getInstance();
        if (!lookup.isReady()) return null;
        const info = lookup.getMSLInfo(sidc);
        return info ? { dr: info.getDrawRule(), name: info.getName(), modifiers: info.getModifiers() } : null;
    } catch (_) { return null; }
}

function parseModifiers(sidcModifiers) {
    if (!sidcModifiers) return {};
    try { return typeof sidcModifiers === 'string' ? JSON.parse(sidcModifiers) : sidcModifiers; }
    catch (_) { return {}; }
}

function NumField({label, value, onChange, placeholder}) {
    return (
        <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="caption" sx={{width: 120, flexShrink: 0, color: 'text.secondary', fontSize: 11}}>
                {label}
            </Typography>
            <TextField
                size="small" fullWidth type="number"
                value={value ?? ''}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                inputProps={{style: {fontSize: 12, padding: '3px 8px'}}}
            />
        </Stack>
    );
}

function StrField({label, value, onChange}) {
    return (
        <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="caption" sx={{width: 120, flexShrink: 0, color: 'text.secondary', fontSize: 11}}>
                {label}
            </Typography>
            <TextField
                size="small" fullWidth type="text"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                inputProps={{style: {fontSize: 12, padding: '3px 8px'}}}
            />
        </Stack>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
const TacticalMarkingEditor = ({feature, onUpdateProperties, onClose}) => {
    const props = feature?.properties ?? {};
    const sidc  = props.sidc;

    const {dr, name, modifiers} = useMemo(() => getDrawRule(sidc) ?? {dr: null, name: null, modifiers: []}, [sidc]);
    const mods = useMemo(() => parseModifiers(props.sidcModifiers), [props.sidcModifiers]);

    const updateMod = useCallback((key, val) => {
        const next = {...mods};
        if (val === '' || val == null) delete next[key];
        else next[key] = String(val);
        onUpdateProperties?.({ sidcModifiers: JSON.stringify(next) });
    }, [mods, onUpdateProperties]);

    if (dr === null) return null;

    const AM  = Modifiers.AM_DISTANCE;
    const AN  = Modifiers.AN_AZIMUTH;
    const amVal = mods[AM] ?? '';
    const anVal = mods[AN] ?? '';

    let fields = null;
    if (isCircular(dr)) {
        const isCircular2 = dr === 902;
        if (isCircular2) {
            // CIRCULAR2 (range fan): up to 3 concentric rings. AM is stored as
            // comma-separated "r1,r2,r3" — WebRenderer accepts multi-value AM.
            const parts = amVal.split(',');
            const [r1, r2, r3] = [parts[0] ?? '', parts[1] ?? '', parts[2] ?? ''];
            const updateRings = (a, b, c) => {
                const joined = [a, b, c].join(',').replace(/,+$/, '');
                updateMod(AM, joined);
            };
            fields = (
                <>
                    <NumField label="Ring 1 radius (m)" value={r1} placeholder="e.g. 1000"
                        onChange={(v) => updateRings(v, r2, r3)}/>
                    <NumField label="Ring 2 radius (m)" value={r2} placeholder="optional"
                        onChange={(v) => updateRings(r1, v, r3)}/>
                    <NumField label="Ring 3 radius (m)" value={r3} placeholder="optional"
                        onChange={(v) => updateRings(r1, r2, v)}/>
                </>
            );
        } else {
            fields = (
                <NumField label="Radius (m)" value={amVal} placeholder="e.g. 1000"
                    onChange={(v) => updateMod(AM, v)}/>
            );
        }
    } else if (isEllipse(dr)) {
        // AM = "semi-major,semi-minor" (comma-separated), AN = rotation degrees
        const [major, minor] = amVal.split(',');
        const updateAxes = (maj, min) => updateMod(AM, maj || minor ? `${maj ?? ''},${min ?? ''}` : '');
        fields = (
            <>
                <NumField label="Semi-major (m)" value={major ?? ''} placeholder="e.g. 2000"
                    onChange={(v) => updateAxes(v, minor)}/>
                <NumField label="Semi-minor (m)" value={minor ?? ''} placeholder="e.g. 1000"
                    onChange={(v) => updateAxes(major, v)}/>
                <NumField label="Rotation (°)" value={anVal} placeholder="0–360"
                    onChange={(v) => updateMod(AN, v)}/>
            </>
        );
    } else if (isRectangular(dr)) {
        // RECTANGULAR1 (dr=801): AM = width (the 2-pt centreline sets the length)
        // RECTANGULAR2 (dr=802): AM1 = target length, AM2 = target width, AN = attitude (mils)
        // RECTANGULAR3 (dr=803): AM = width only
        const isRect2 = dr === 802;
        if (isRect2) {
            // Always show two-part form for RECTANGULAR2 regardless of stored value
            const [len, wid] = amVal.includes(',') ? amVal.split(',') : ['', ''];
            const updateLW = (l, w) => updateMod(AM, l || w ? `${l ?? ''},${w ?? ''}` : '');
            fields = (
                <>
                    <NumField label="Target length (m)" value={len ?? ''} placeholder="e.g. 2000"
                        onChange={(v) => updateLW(v, wid)}/>
                    <NumField label="Target width (m)"  value={wid ?? ''} placeholder="e.g. 1000"
                        onChange={(v) => updateLW(len, v)}/>
                    <NumField label="Orientation (°)" value={anVal} placeholder="0–6400 mils"
                        onChange={(v) => updateMod(AN, v)}/>
                </>
            );
        } else {
            // RECTANGULAR1 and RECTANGULAR3: single AM = width
            fields = (
                <NumField label="Width (m)" value={amVal} placeholder="e.g. 1000"
                    onChange={(v) => updateMod(AM, v)}/>
            );
        }
    } else if (isArc(dr)) {
        // AN = "left,right" azimuth degrees, AM = range metres
        const [leftAz, rightAz] = anVal.split(',');
        const updateAzimuths = (l, r) => updateMod(AN, l || r ? `${l ?? ''},${r ?? ''}` : '');
        fields = (
            <>
                <NumField label="Range (m)" value={amVal} placeholder="e.g. 5000"
                    onChange={(v) => updateMod(AM, v)}/>
                <NumField label="Left azimuth (°)" value={leftAz ?? ''} placeholder="0–360"
                    onChange={(v) => updateAzimuths(v, rightAz)}/>
                <NumField label="Right azimuth (°)" value={rightAz ?? ''} placeholder="0–360"
                    onChange={(v) => updateAzimuths(leftAz, v)}/>
            </>
        );
    } else if (isPoint17(dr)) {
        // POINT17 rectangle: AM = "length,width" (comma-separated metres), AN = rotation degrees
        const [len, wid] = amVal.split(',');
        const updateLW = (l, w) => updateMod(AM, l || w ? `${l ?? ''},${w ?? ''}` : '');
        fields = (
            <>
                <NumField label="Length (m)" value={len ?? ''} placeholder="e.g. 2000"
                    onChange={(v) => updateLW(v, wid)}/>
                <NumField label="Width (m)"  value={wid ?? ''} placeholder="e.g. 1000"
                    onChange={(v) => updateLW(len, v)}/>
                <NumField label="Rotation (°)" value={anVal} placeholder="0–360 (0 = North, CW+)"
                    onChange={(v) => updateMod(AN, v)}/>
            </>
        );
    } else if (isPoint18(dr)) {
        // POINT18 search-area fan: AN = search-axis azimuth, AM = "start,stop" range in metres
        const [startR, stopR] = amVal.split(',');
        const updateRanges = (s, e) => updateMod(AM, s || e ? `${s ?? ''},${e ?? ''}` : '');
        fields = (
            <>
                <NumField label="Search azimuth (°)" value={anVal} placeholder="0–360"
                    onChange={(v) => updateMod(AN, v)}/>
                <NumField label="Start range (m)" value={startR ?? ''} placeholder="e.g. 0"
                    onChange={(v) => updateRanges(v, stopR)}/>
                <NumField label="Stop range (m)" value={stopR ?? ''} placeholder="e.g. 5000"
                    onChange={(v) => updateRanges(startR, v)}/>
            </>
        );
    } else if (isCorridor(dr)) {
        fields = (
            <NumField label="Half-width (m)" value={amVal} placeholder="e.g. 500"
                onChange={(v) => updateMod(AM, v)}/>
        );
    }

    // For any other tactical symbol (line/area with no parametric modifiers):
    // just show name + label field.
    const label = props.label ?? '';

    // Keys already covered by the Dimensions section — skip in Properties
    const DIMENSION_KEYS = new Set([Modifiers.AM_DISTANCE, Modifiers.AN_AZIMUTH]);

    const propFields = modifiers.filter(key => !DIMENSION_KEYS.has(key) && MODIFIER_LABELS[key]);

    return (
        <Stack sx={{height: '100%', overflow: 'hidden'}}>
            <Stack direction="row" alignItems="center" sx={(theme) => ({
                px: 1.5, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}`,
                flexShrink: 0, bgcolor: 'background.default',
            })}>
                <Typography variant="caption" sx={{fontWeight: 700, flex: 1, fontSize: 11,
                    letterSpacing: 0.3, textTransform: 'uppercase', color: 'text.disabled'}}>
                    Tactical Graphic
                </Typography>
                {onClose && (
                    <Box component="button" onClick={onClose}
                        sx={{border: 'none', background: 'none', cursor: 'pointer', p: 0.25,
                            color: 'text.secondary', '&:hover': {color: 'text.primary'}}}>
                        ✕
                    </Box>
                )}
            </Stack>

            <Box sx={{flex: 1, overflowY: 'auto', px: 1.5, py: 1}}>
                <Stack gap={1.25}>
                    {name && (
                        <Typography sx={{fontSize: 11, fontWeight: 600, color: 'text.primary', lineHeight: 1.3}}>
                            {name}
                        </Typography>
                    )}
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="caption" sx={{width: 120, flexShrink: 0, color: 'text.secondary', fontSize: 11}}>
                            Label
                        </Typography>
                        <TextField size="small" fullWidth value={label}
                            onChange={(e) => onUpdateProperties?.({label: e.target.value})}
                            inputProps={{style: {fontSize: 12, padding: '3px 8px'}}}/>
                    </Stack>

                    {fields && (
                        <>
                            <Typography variant="caption" sx={{fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                                textTransform: 'uppercase', color: 'text.disabled', pt: 0.5}}>
                                Dimensions
                            </Typography>
                            {fields}
                        </>
                    )}

                    {propFields.length > 0 && (
                        <>
                            <Typography variant="caption" sx={{fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                                textTransform: 'uppercase', color: 'text.disabled', pt: 0.5}}>
                                Properties
                            </Typography>
                            {propFields.map((key) => (
                                <StrField
                                    key={key}
                                    label={MODIFIER_LABELS[key]}
                                    value={mods[key] ?? ''}
                                    onChange={(v) => updateMod(key, v)}
                                />
                            ))}
                        </>
                    )}
                </Stack>
            </Box>
        </Stack>
    );
};

export default TacticalMarkingEditor;
